import {
  Component,
  OnDestroy,
  OnInit,
  Inject,
  ChangeDetectorRef,
} from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { ICascadingConfig } from '../../../models/irembo-cascading-config';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { IEnvironment } from '../../../models/environment.model';
import { getValueFromNestedObject } from '../../../../utils/utils/nested-object-extractor.util';
import { ToastService } from '../../../services/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { FormStateService } from '../../../services/formly/form-state.service';
import {
  populateFormFields,
  getPopulateReferenceForm,
} from '../../../../utils/utils/data-fetch-widget-utils';

export interface EditableInputValueChange {
  status: boolean;
  previousValue: any;
  newValue: any;
  datasetValueName?: string;
  datasetValueCode?: string;
}

@Component({
  selector: 'irembogov-custom-cascading-editable-input',
  templateUrl: './custom-cascading-editable-input.component.html',
})
export class CustomCascadingEditableInputComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy
{
  cascadingConfigs: ICascadingConfig[] = [];
  items: Record<string, Record<string, unknown>[] | null> = {};
  loading: Record<string, boolean> = {};
  private readonly subscriptions = new Subscription();
  private readonly environment: IEnvironment;
  private initialValues: Record<string, any> = {};

  constructor(
    @Inject('environment') environment: IEnvironment,
    private readonly http: HttpClient,
    private readonly cd: ChangeDetectorRef,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly formStateService: FormStateService
  ) {
    super();
    this.environment = environment;
  }

  ngOnInit(): void {
    if (this.props?.['cascadingConfigs']) {
      this.cascadingConfigs = this.props[
        'cascadingConfigs'
      ] as ICascadingConfig[];
    }
    this.loadInitialData();
  }

  onActiveDropdownChange(config: ICascadingConfig): void {
    this.fetchData(config);
  }

  private loadInitialData(): void {
    const formControlValue = this.field?.formControl?.value || {};
    const initializedLocationKeys = this.props?.[
      'initializedLocationKeys'
    ] as Record<string, string>;
    const model = this.field?.parent?.model;

    if (!this.cascadingConfigs.length) {
      return;
    }

    this.initialValues = { ...formControlValue };

    if (initializedLocationKeys && model) {
      for (const [configKey, modelKey] of Object.entries(
        initializedLocationKeys
      )) {
        if (this.initialValues[configKey] === undefined) {
          const modelValue = model[modelKey];
          if (modelValue !== undefined) {
            this.initialValues[configKey] = modelValue;
          }
        }
      }
    }

    let processPromise = Promise.resolve();
    const baseFields = this.cascadingConfigs.filter(config => !config.parent);

    processPromise = baseFields.reduce((promise, config) => {
      return promise.then(() => this.loadFieldAndChildren(config));
    }, processPromise);

    processPromise
      .then(() => {
        const nonNullValues = Object.fromEntries(
          Object.entries(this.initialValues).filter(
            ([key, value]) =>
              value !== null &&
              (typeof value !== 'object' || value.newValue !== null)
          )
        );
        if (Object.keys(nonNullValues).length > 0 && this.formControl) {
          this.formControl.patchValue(nonNullValues);
        }
        this.cd.detectChanges();
      })
      .catch(error => {
        console.error('Error in initialization sequence:', error);
      });
  }

  private loadFieldAndChildren(config: ICascadingConfig): Promise<void> {
    const rawValue = this.initialValues[config.key];
    const isNullValue =
      rawValue === null ||
      (rawValue && typeof rawValue === 'object' && rawValue.newValue === null);

    const shouldFetch =
      !config.parent ||
      (config.parent && this.formControl?.value?.[config.parent]);

    if (!shouldFetch) {
      return Promise.resolve();
    }

    return this.fetchData(config)
      .then(() => {
        if (!isNullValue) {
          const mappedValue = this.mapValue(config, rawValue);
          if (mappedValue && this.formControl) {
            this.formControl.patchValue({ [config.key]: mappedValue });
            this.cd.detectChanges();
          }
        }

        if (isNullValue) {
          return Promise.resolve();
        }

        const children = this.cascadingConfigs.filter(
          c => c.parent === config.key
        );

        return children.reduce((promise, childConfig) => {
          return promise.then(() => {
            const parentValue = this.formControl?.value
              ? this.formControl.value[config.key]
              : null;

            if (parentValue) {
              return this.loadFieldAndChildren({
                ...childConfig,
                value: parentValue,
              });
            }
            return Promise.resolve();
          });
        }, Promise.resolve());
      })
      .catch(error => {
        console.warn(`Error loading field ${config.key}:`, error);
        return Promise.resolve();
      });
  }

  private fetchData(config: ICascadingConfig): Promise<void> {
    if (!config || this.loading[config.key]) {
      return Promise.resolve();
    }

    let searchId: unknown = config.value;
    if (
      typeof config.value === 'object' &&
      config.value &&
      config.parentBindKey
    ) {
      searchId = config.value[config.parentBindKey];
    }

    if (config.parent && (searchId === null || searchId === undefined)) {
      return Promise.resolve();
    }

    const hasParentValue = !!searchId;
    const shouldRefresh = hasParentValue;

    if (this.items[config.key] && shouldRefresh) {
      this.items[config.key] = null;
    } else if (this.items[config.key] && !shouldRefresh) {
      return Promise.resolve();
    }

    this.loading[config.key] = true;

    let url = searchId
      ? `${config.dataset.url}/${searchId}`
      : config.dataset.url;

    if (config.dataset.useBaseUrl) {
      try {
        url = this.prependUrlWithApiGatewayBaseUrl(url);
      } catch (error) {
        this.loading[config.key] = false;
        return Promise.reject(
          error instanceof Error
            ? error
            : new Error('Failed to prepend API Gateway base URL')
        );
      }
    }

    return new Promise((resolve, reject) => {
      this.subscriptions.add(
        this.http.get<Record<string, unknown> | []>(url).subscribe({
          next: res => {
            try {
              const datasetResponse = Array.isArray(res)
                ? res
                : getValueFromNestedObject(res, config.dataset.dataField) ?? [];

              const bindlabel = config.bindlabel ?? 'name';
              const data = Array.isArray(datasetResponse)
                ? datasetResponse.map(item => ({
                    ...item,
                    [bindlabel]: this.translateService.instant(
                      String(item[bindlabel] ?? '')
                    ),
                  }))
                : [];

              this.items[config.key] = data;
              this.loading[config.key] = false;
              this.cd.detectChanges();
              resolve();
            } catch (error) {
              this.loading[config.key] = false;
              this.cd.detectChanges();
              reject(
                error instanceof Error
                  ? error
                  : new Error('Failed to process dataset response')
              );
            }
          },
          error: err => {
            this.loading[config.key] = false;
            this.cd.detectChanges();

            if (searchId !== null && searchId !== undefined) {
              this.toastService.show({
                body: `Error loading ${config.label ?? 'data'}`,
                type: 'error',
              });
            }
            reject(
              err instanceof Error
                ? err
                : new Error(`Failed to fetch data for ${config.key}`)
            );
          },
        })
      );
    });
  }

  private stripLeadingZeros(value: string): string {
    return String(value).replace(/^0+/, '') || '0';
  }

  private mapValue(config: ICascadingConfig, rawValue: any): any {
    if (
      !rawValue ||
      !this.items[config.key] ||
      !Array.isArray(this.items[config.key])
    ) {
      return null;
    }

    const items = this.items[config.key] as Record<string, unknown>[];
    const bindValueKey = config.bindvalue ?? 'id';
    const bindLabelKey = config.bindlabel ?? 'name';
    const codeKey = 'value';

    // Handle EditableInputValueChange format
    if (rawValue && typeof rawValue === 'object' && 'newValue' in rawValue) {
      const newValue = rawValue.newValue;
      if (!newValue) {
        return null;
      }

      const matchingItem = items.find(item => {
        return (
          (item[bindValueKey] !== undefined &&
            newValue[bindValueKey] !== undefined &&
            this.stripLeadingZeros(String(item[bindValueKey])) ===
              this.stripLeadingZeros(String(newValue[bindValueKey]))) ||
          (item[bindLabelKey] !== undefined &&
            newValue[bindLabelKey] !== undefined &&
            String(item[bindLabelKey]) === String(newValue[bindLabelKey])) ||
          (item[codeKey] !== undefined &&
            newValue[codeKey] !== undefined &&
            this.stripLeadingZeros(String(item[codeKey])) ===
              this.stripLeadingZeros(String(newValue[codeKey])))
        );
      });

      return matchingItem ?? null;
    }

    // Handle string or number values
    if (typeof rawValue === 'string' || typeof rawValue === 'number') {
      const normalizedValue = this.stripLeadingZeros(String(rawValue));
      const matchingItem = items.find(item => {
        if (
          bindValueKey &&
          this.stripLeadingZeros(String(item[bindValueKey] ?? '')) ===
            normalizedValue
        ) {
          return true;
        }
        if (String(item[bindLabelKey] ?? '') === String(rawValue)) {
          return true;
        }
        if (
          this.stripLeadingZeros(String(item[codeKey] ?? '')) ===
          normalizedValue
        ) {
          return true;
        }
        const normalizedLabel = this.normalizeNamePattern(
          String(item[bindLabelKey] ?? '')
        );
        return normalizedLabel === this.normalizeNamePattern(String(rawValue));
      });

      return matchingItem ?? null;
    }

    return null;
  }

  private prependUrlWithApiGatewayBaseUrl(url: string): string {
    const apiGatewayBaseUrl = this.environment?.apiGatewayBaseUrl;
    if (!apiGatewayBaseUrl) {
      throw new Error('apiGatewayBaseUrl is required');
    }
    return `${apiGatewayBaseUrl}${url}`;
  }

  private normalizeNamePattern(name: string): string {
    if (!name || typeof name !== 'string') return '';

    const limitedName = name.substring(0, 1000);
    const lastSpaceIndex = limitedName.lastIndexOf(' ');

    if (lastSpaceIndex === -1) {
      return limitedName.trim();
    }

    const beforeSpace = limitedName.substring(0, lastSpaceIndex);
    const afterSpace = limitedName.substring(lastSpaceIndex + 1);
    const isRomanNumeral = /^[IVX]{1,10}$/i.test(afterSpace);

    if (isRomanNumeral) {
      return beforeSpace.trim() + ' ' + afterSpace.toUpperCase();
    }

    return limitedName.trim();
  }

  onValueChange(event: {
    formValue: Record<string, any>;
    data: Record<string, any>;
  }): void {
    if (!event || !this.formControl) return;

    this.formControl.setValue(event.formValue ?? {});

    const hasEdits = Object.entries(event.data ?? {}).some(
      ([key, value]) => key.endsWith('_status') && value === true
    );

    if (hasEdits) {
      this.formControl.markAsDirty();
    }

    const populatesConfig = this.field.props?.['populates'];
    if (
      populatesConfig &&
      Array.isArray(populatesConfig) &&
      populatesConfig.length > 0
    ) {
      const populateForm = getPopulateReferenceForm(this.field, this.form);

      if (populateForm) {
        this.formStateService.saveFetchedDataKeyInFormState(
          this.field,
          event.data ?? {}
        );
        populateFormFields(
          event.data ?? {},
          this.field,
          populateForm,
          this.formStateService
        );
      }
    }

    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
